import { useCallback, useEffect, useRef } from 'react';

import useAudioPlayer from '@/shared/hooks/useAudioPlayer';

import type { TracingGameData } from '../types';

type TracingGameVoiceLine = 'intro' | 'complete' | 'fail' | 'inactive' | 'skip';

const VOICE_PRIORITY: Record<TracingGameVoiceLine, number> = { intro: 1, complete: 1, fail: 1, inactive: 0, skip: 0 };

export const useTracingGameVoiceAudio = (
	voiceAudio: TracingGameData['presentation']['voice_audio'],
	audioOn: boolean,
) => {
	const completionRef = useRef<(() => void) | null>(null);
	const completePending = useCallback(() => {
		const onComplete = completionRef.current;
		completionRef.current = null;
		onComplete?.();
	}, []);
	const {
		play: playIntro,
		stop: stopIntro,
		playing: isIntroPlaying,
	} = useAudioPlayer({
		src: voiceAudio?.intro_audio_url,
		onEnd: completePending,
	});
	const {
		play: playComplete,
		stop: stopComplete,
		playing: isCompletePlaying,
	} = useAudioPlayer({
		src: voiceAudio?.complete_audio_url,
		onEnd: completePending,
	});
	const {
		play: playFail,
		stop: stopFail,
		playing: isFailPlaying,
	} = useAudioPlayer({
		src: voiceAudio?.fail_audio_url,
		onEnd: completePending,
	});

	const {
		play: playInactive,
		stop: stopInactive,
		playing: isInactivePlaying,
	} = useAudioPlayer({
		src: voiceAudio?.inactive_audio_url,
		onEnd: completePending,
	});

	const {
		play: playSkip,
		stop: stopSkip,
		playing: isSkipPlaying,
	} = useAudioPlayer({
		src: voiceAudio?.skip_audio_url,
		onEnd: completePending,
	});

	const stop = useCallback(() => {
		completionRef.current = null;
		stopIntro();
		stopComplete();
		stopFail();
		stopInactive();
		stopSkip();
	}, [stopComplete, stopFail, stopInactive, stopIntro, stopSkip]);

	const play = useCallback(
		(voiceLine: TracingGameVoiceLine, onComplete?: () => void) => {
			const players = {
				intro: {
					url: voiceAudio?.intro_audio_url,
					play: playIntro,
					playing: isIntroPlaying,
					priority: VOICE_PRIORITY.intro,
				},
				complete: {
					url: voiceAudio?.complete_audio_url,
					play: playComplete,
					playing: isCompletePlaying,
					priority: VOICE_PRIORITY.complete,
				},
				fail: {
					url: voiceAudio?.fail_audio_url,
					play: playFail,
					playing: isFailPlaying,
					priority: VOICE_PRIORITY.fail,
				},
				inactive: {
					url: voiceAudio?.inactive_audio_url,
					play: playInactive,
					playing: isInactivePlaying,
					priority: VOICE_PRIORITY.inactive,
				},
				skip: {
					url: voiceAudio?.skip_audio_url,
					play: playSkip,
					playing: isSkipPlaying,
					priority: VOICE_PRIORITY.skip,
				},
			};
			const requested = players[voiceLine];
			const blocked = Object.values(players).some(
				(player) => player.playing?.() && (player.priority > requested.priority || requested.priority === 0),
			);
			if (blocked) {
				onComplete?.();
				return;
			}
			stop();
			if (!audioOn || !requested.url) {
				onComplete?.();
				return;
			}
			completionRef.current = onComplete ?? null;

			requested.play();
		},
		[
			audioOn,
			isCompletePlaying,
			isFailPlaying,
			isInactivePlaying,
			isIntroPlaying,
			isSkipPlaying,
			playComplete,
			playFail,
			playInactive,
			playIntro,
			playSkip,
			stop,
			voiceAudio,
		],
	);

	useEffect(() => {
		if (!audioOn) {
			completePending();
			stop();
		}
	}, [audioOn, completePending, stop]);

	useEffect(() => () => stop(), [stop]);

	return { play, stop };
};
